import {
    GeoJSONSource,
    GeoJSONSourceSpecification,
    LayerSpecification,
    Map,
} from "mapbox-gl";
import * as turf from "@turf/turf";
import {
    Feature,
    LineString,
    MultiPolygon,
    Point,
    Polygon,
    Position,
} from "geojson";
import { isLocalhost } from "./env";

type PolygonLike = Feature<Polygon | MultiPolygon>;

function outerRing(feature: PolygonLike): Position[] | null {
    const geometry = feature.geometry;
    if (!geometry) return null;
    if (geometry.type === "Polygon") {
        return geometry.coordinates[0] ?? null;
    }
    return geometry.coordinates[0]?.[0] ?? null;
}

function longestEdgeMeters(ring: Position[]): number {
    let longest = 0;
    for (let i = 0; i < ring.length - 1; i++) {
        const edgeLength = turf.distance(ring[i], ring[i + 1], {
            units: "meters",
        });
        if (edgeLength > longest) {
            longest = edgeLength;
        }
    }
    return longest;
}

export function polygonShortSide(feature: PolygonLike): number {
    const ring = outerRing(feature);
    if (!ring || ring.length < 2) return 0;
    const longest = longestEdgeMeters(ring);
    if (longest <= 0) return 0;
    return turf.area(feature) / longest;
}

function minVertexToLineDistanceMeters(
    vertices: Position[],
    line: Feature<LineString>,
): number {
    let min = Infinity;
    for (const vertex of vertices) {
        const nearest = turf.nearestPointOnLine(line, vertex, {
            units: "meters",
        });
        const dist = nearest.properties?.dist ?? Infinity;
        if (dist < min) {
            min = dist;
        }
    }
    return min;
}

export function polygonDistance(a: PolygonLike, b: PolygonLike): number {
    if (turf.booleanIntersects(a, b)) {
        return 0;
    }

    const ringA = outerRing(a);
    const ringB = outerRing(b);
    if (!ringA || !ringB) return Infinity;

    const lineA = turf.polygonToLine(a);
    const lineB = turf.polygonToLine(b);
    const linesA = (
        lineA.type === "FeatureCollection" ? lineA.features : [lineA]
    ) as Feature<LineString>[];
    const linesB = (
        lineB.type === "FeatureCollection" ? lineB.features : [lineB]
    ) as Feature<LineString>[];

    let min = Infinity;
    for (const line of linesB) {
        min = Math.min(min, minVertexToLineDistanceMeters(ringA, line));
    }
    for (const line of linesA) {
        min = Math.min(min, minVertexToLineDistanceMeters(ringB, line));
    }
    return min;
}

export function polygonsOverlap(a: PolygonLike, b: PolygonLike): boolean {
    if (!turf.booleanIntersects(a, b)) return false;
    try {
        const intersection = turf.intersect(
            turf.featureCollection([
                a as Feature<Polygon | MultiPolygon>,
                b as Feature<Polygon | MultiPolygon>,
            ]),
        );
        if (!intersection) return false;
        return turf.area(intersection) > 1;
    } catch {
        return (
            turf.booleanOverlap(a, b) ||
            turf.booleanContains(a, b) ||
            turf.booleanContains(b, a)
        );
    }
}

export function addUpdateGeojsonSource(
    map: Map,
    source: string,
    data: GeoJSONSourceSpecification,
) {
    if (!map) return;
    const s = map.getSource(source) as GeoJSONSource;
    if (s) {
        if (data.data) {
            s.setData(data.data);
        }
    } else {
        map.addSource(source, data);
    }
}

export function addUpdateGeojsonLayers(map: Map, layers: LayerSpecification[]) {
    if (!map) return;
    layers.forEach((layer) => {
        const l = map.getLayer(layer.id);
        if (!l) {
            map.addLayer(layer);
        }
    });
}

export function removeLayersIfExists(map: Map, layerIds: string[]) {
    if (!map) return;
    layerIds?.forEach((layerId) => {
        const l = map.getLayer(layerId);
        if (l) {
            map.removeLayer(layerId);
        }
    });
}

export function removeSourcesIfExists(map: Map, sourceIds: string[]) {
    if (!map) return;
    sourceIds?.forEach((sourceId) => {
        const s = map.getSource(sourceId) as GeoJSONSource;
        if (s) {
            map.removeSource(sourceId);
        }
    });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function invertedTurfBboxToFourCornerCoordinates(boundingBox: any) {
    if (!boundingBox || boundingBox.length !== 4) return null;
    const [minX, minY, maxX, maxY] = boundingBox;
    const topLeft = [minX, maxY];
    const topRight = [maxX, maxY];
    const bottomRight = [maxX, minY];
    const bottomLeft = [minX, minY];

    return [bottomLeft, bottomRight, topRight, topLeft];
}

export function updateLayerOpacity(
    map: Map,
    layerIds: string[],
    opacity: number,
) {
    layerIds.forEach((layerId) => {
        const layer = map.getLayer(layerId);
        if (layer) {
            if (["fill", "line", "raster"].includes(layer.type)) {
                const paintProperty = `${layer.type}-opacity`;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                map.setPaintProperty(layerId, paintProperty as any, opacity);
            }
        }
    });
}

export function updateLayerVisibility(
    map: Map,
    layerIds: string[],
    visibility: boolean,
) {
    if (!map) return;
    layerIds.forEach((layerId) => {
        const layer = map.getLayer(layerId);
        if (layer) {
            map.setLayoutProperty(
                layerId,
                "visibility",
                visibility ? "visible" : "none",
            );
        }
    });
}

export function moveLayersToTop(map: Map, layerIds: string[]) {
    if (!map) return;

    layerIds.forEach((layerId) => {
        if (!map.getLayer(layerId)) {
            return;
        }

        map.moveLayer(layerId);
    });
}

export type Coordinates = [
    [number, number],
    [number, number],
    [number, number],
    [number, number],
];

export function getPointInBoundary(
    point: Point,
    fieldBoundary?: Feature<MultiPolygon>,
): Point {
    if (!fieldBoundary) return point;
    if (!turf.booleanPointInPolygon(point, fieldBoundary)) {
        const nearestPoint = turf.nearestPoint(
            point,
            turf.explode(fieldBoundary),
        );
        return nearestPoint.geometry as Point;
    }
    return point;
}
export function getPointOnBoundary(
    point: Point,
    fieldBoundary?: Feature<MultiPolygon>,
): Point {
    if (!fieldBoundary) return point;
    return turf.nearestPointOnLine(
        turf.polygonToLine(fieldBoundary) as Feature<LineString>,
        point,
        { units: "feet" },
    ).geometry as Point;
}

export const getMapboxToken = () => {
    if (isLocalhost()) {
        return import.meta.env.VITE_MAPBOX_TOKEN ?? "";
    }
    return "pk.eyJ1Ijoic2VudGluZWxmZXJ0IiwiYSI6ImNtbTI2eDBpNDA0bnMyc3ByeTk0cHg5ZjIifQ.cKfb0TvvScThGQ3LITinXg";
};

export function buildFieldMapTilesUrl(
    apiBaseUrl: string,
    filters: Record<string, string | number> = {},
): string {
    const base = apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`;
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value != null && value !== "") {
            params.set(key, String(value));
        }
    });
    const query = params.toString();
    const path = `fields/tiles/{z}/{x}/{y}/${query ? `?${query}` : ""}`;
    if (base.startsWith("http")) {
        return `${base}${path}`;
    }
    return `${window.location.origin}${base}${path}`;
}

export function buildPlssTilesUrl(apiBaseUrl: string): string {
    const base = apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`;
    const path = "plss/tiles/{z}/{x}/{y}/";
    if (base.startsWith("http")) {
        return `${base}${path}`;
    }
    return `${window.location.origin}${base}${path}`;
}

export function isApiTileRequestUrl(url: string): boolean {
    return url.includes("/fields/tiles/") || url.includes("/plss/tiles/");
}
